/* globals zoomSdk */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import pexels, { proxyPexelsImageUrl } from '../util/pexels'
import { updateStatus } from '../root/actions'
import { SPACE_OPTIONS } from '../clientSetting/constants'
import * as constants from './constants'
import {
  base64ArrayBuffer,
  convertToDataUrl,
  getImageDataFromFileUrl,
} from '../util/imageDataConversion'

const isSuccessResponse = (response) => {
  if (response === 'Success') {
    return true
  }

  if (typeof response === 'string') {
    return response.toLowerCase() === 'success'
  }

  if (!response || typeof response !== 'object') {
    return false
  }

  return [response.message, response.status, response.result].some(
    (value) => typeof value === 'string' && value.toLowerCase() === 'success'
  )
}

const formatSdkResponse = (response) => {
  if (typeof response === 'string') {
    return response
  }

  try {
    return JSON.stringify(response)
  } catch (error) {
    return String(response)
  }
}

const getClientSettingBackgroundName = (state) => {
  const { query, recommendation, space } = state.clientSetting || {}
  const selectedSpace = SPACE_OPTIONS.find((option) => option.id === space)

  return query?.trim() || recommendation?.trim() || selectedSpace?.label || ''
}

const ZAPP_ABILITY_AI_VB_NEED_IMAGE_DATA = 1 << 0

const needsImageData = (state) => {
  const ability = state.app?.zaConfig?.ability ?? 0
  return (ability & ZAPP_ABILITY_AI_VB_NEED_IMAGE_DATA) !== 0
}

const buildVirtualBackgroundParams = async ({
  fileUrl,
  includeRawImageData,
  name,
  rawImageData,
}) => {
  if (includeRawImageData && rawImageData) {
    return {
      imageData: rawImageData,
      name,
      type: 'image',
    }
  }

  const imageData = await getImageDataFromFileUrl(fileUrl)

  return {
    bitmap:
      'data:object/Uint8ClampedArray;base64,' +
      base64ArrayBuffer(imageData.data),
    height: imageData.height,
    name,
    options: { colorModel: 'RGBA', padding: 0 },
    type: 'image',
    width: imageData.width,
  }
}

export const setVirtualBackground = createAsyncThunk(
  'images/setVirtualBackground',
  async ({ image, popPreviousImage = false }, { dispatch, getState }) => {
    const {
      vb: { image: currentImage },
    } = getState()

    dispatch(addPreviousImage(currentImage))

    const state = getState()
    const name = getClientSettingBackgroundName(state)
    const includeRawImageData = needsImageData(state)

    let sdkResponse
    const shouldTrackDownload = Boolean(image?.links?.download_location)
    /**
     *  Upon the initial app load, we initially save only the background ID (Client generated Id) in the "previousImage"
     * When a user clicks "undo", we utilize this background ID to invoke the "setVirtualBackground" function without having to call getVirtualBackgroundData.
     * Nevertheless, when a user initiates the "setVirtualBackground" operation with a different Pexels image, we store the complete Pexels image in the "previousImage" variable
     */
    if (!image.src && image.id) {
      /**
       * Currently, "setVirtualBackground" with "backgroundId" does not work. Although "callZoomApi" works, it triggers a consent dialog.
       * "callZoomApi" will be used as a temporary solution until Client team fix the API
       */
      sdkResponse = await zoomSdk.callZoomApi(
        'setVirtualBackground',
        { backgroundId: image.id, name },
        30000
      )
    } else if (image.src?.landscape || image.src?.dataUrl) {
      const fileUrl = image.src.landscape
        ? proxyPexelsImageUrl(image.src.landscape)
        : image.src.dataUrl
      const params = await buildVirtualBackgroundParams({
        fileUrl,
        includeRawImageData,
        name,
        rawImageData: image.rawImageData,
      })
      sdkResponse = await zoomSdk.callZoomApi('setVirtualBackground', params)
    }

    const isSuccessfulResponse = isSuccessResponse(sdkResponse)

    if (isSuccessfulResponse) {
      if (shouldTrackDownload) {
        pexels.photos.trackDownload(image)
      }
      dispatch(updateStatus(constants.SET_BACKGROUND_SUCCESS))
    } else {
      dispatch(updateStatus(constants.SET_BACKGROUND_FAILURE))
    }

    if (popPreviousImage) {
      dispatch(deletePreviousImage())
    }

    if (!isSuccessfulResponse) {
      throw new Error(
        `setVirtualBackground failed: ${formatSdkResponse(sdkResponse)}`
      )
    }

    return sdkResponse
  }
)

export const removeVirtualBackground = createAsyncThunk(
  'images/removeVirtualBackground',
  async ({ popPreviousImage = false } = {}, { dispatch, getState }) => {
    const {
      vb: { image: currentImage },
    } = getState()

    dispatch(addPreviousImage(currentImage))

    const sdkResponse = await zoomSdk.callZoomApi(
      'removeVirtualBackground',
      null,
      30000
    )
    if (sdkResponse === 'Success') {
      dispatch(updateStatus(constants.REMOVE_BACKGROUND_SUCCESS))
    } else {
      dispatch(updateStatus(constants.REMOVE_BACKGROUND_FAILURE))
    }

    if (popPreviousImage) {
      dispatch(deletePreviousImage())
    }

    return sdkResponse
  }
)

export const blurVirtualBackground = createAsyncThunk(
  'images/blurVirtualBackground',
  async ({ popPreviousImage = false } = {}, { dispatch, getState }) => {
    const {
      vb: { image: currentImage },
    } = getState()

    dispatch(addPreviousImage(currentImage))

    const sdkResponse = await zoomSdk.setVirtualBackground({ blur: true })
    if (sdkResponse === 'Success') {
      dispatch(updateStatus(constants.BLUR_BACKGROUND_SUCCESS))
    } else {
      dispatch(updateStatus(constants.BLUR_BACKGROUND_FAILURE))
    }

    if (popPreviousImage) {
      dispatch(deletePreviousImage())
    }

    return sdkResponse
  }
)

export const getCurrentBackground = createAsyncThunk(
  'images/getCurrentBackground',
  async () => {
    const response = await zoomSdk.getCurrentVirtualBackground()
    if (response.currentBackgroundSetting === 'background') {
      return { id: response.currentBackground.id }
    }
    return { type: response.currentBackgroundSetting }
  }
)

export const clearToastStates = createAction('images/clearToastStates')

export const setCurrentImage = createAsyncThunk(
  'image/setCurrentImage',
  async (obj, { dispatch, getState }) => {
    const {
      vb: { image: currentImage },
    } = getState()

    dispatch(addPreviousImage(currentImage))
    return obj
  }
)

export const addPreviousImage = createAction(
  'image/addPreviousImage',
  (obj) => {
    return { payload: obj }
  }
)

export const deletePreviousImage = createAction('image/deletePreviousImage')

export const setIsVbSettingImage = createAction(
  'image/setIsVbSettingImage',
  (isSetting) => {
    return { payload: isSetting }
  }
)

export const generateTextToImages = createAsyncThunk(
  'images/generateTextToImages',
  async (options) => {
    const sdkResponse = await zoomSdk.callZoomApi('zmTextToImage', options)
    return sdkResponse
  }
)

export const setTextToImages = createAsyncThunk(
  'images/setTextToImages',
  async (images, { getState }) => {
    const errorMessage = getState().clientSetting?.textToImagesError
    if (errorMessage || !images.length) return []

    const normalizedImages = await Promise.all(
      images.map(async ({ id, imageData }) => ({
        id,
        rawImageData: imageData,
        src: { dataUrl: await convertToDataUrl(imageData) },
      }))
    )

    return normalizedImages
  }
)

export const addVirtualBackground = createAsyncThunk(
  'images/addVirtualBackground',
  async (imageOrFileUrl, { getState }) => {
    const state = getState()
    const name = getClientSettingBackgroundName(state)
    const includeRawImageData = needsImageData(state)
    const fileUrl =
      typeof imageOrFileUrl === 'string'
        ? imageOrFileUrl
        : imageOrFileUrl?.src?.dataUrl
    const rawImageData =
      typeof imageOrFileUrl === 'string'
        ? undefined
        : imageOrFileUrl?.rawImageData
    const params = await buildVirtualBackgroundParams({
      fileUrl,
      includeRawImageData,
      name,
      rawImageData,
    })
    const sdkResponse = await zoomSdk.callZoomApi(
      'addVirtualBackground',
      params
    )
    return sdkResponse
  }
)

export const setTextToImageError = createAction('image/setTextToImageError')

export const resetTextToImage = createAction('image/resetTextToImage')

export const resetAddImage = createAction('image/resetAddImage')
